// @flow
import { mergeDeepRight } from 'ramda'
import type { Store } from 'effector'
import { combine, forward, sample } from 'effector'
import { AppOptionsService } from 'services/app-options'
import { User } from '../entities'
import { DEFAULT_USER } from '../constants'
import { domain } from './domain'
import {
  initialize,
  getUserFx,
  updateUser,
  convertUserTypeFx,
  uploadAvatarFx,
  changeUser,
  reset,
  getUserMeFx,
  getUserProfileFx,
} from './events'

const defaultUser = new User(DEFAULT_USER)

// Create store
const $windowUser: Store<User> = AppOptionsService.getStore().map(
  (options) => new User(mergeDeepRight(DEFAULT_USER, options.user ?? {}))
)
const $user: Store<User> = domain.createStore(defaultUser)
const isLoading: Store<boolean> = domain.createStore(true)
const isLoaded: Store<boolean> = domain.createStore(false)
const profileIsLoaded: Store<boolean> = domain.createStore(false)
const isAvatarLoading = uploadAvatarFx.pending

isLoading
  .on(getUserFx, () => true)
  .on(getUserMeFx, () => true)
  .on(getUserFx.done, () => false)
  .on(getUserMeFx.done, () => false)
  .on(getUserFx.fail, () => false)
  .on(getUserMeFx.fail, () => false)

$user
  .on(getUserFx.done, (state, { result }) => new User(mergeDeepRight(state, result)))
  .on(getUserProfileFx.done, (state, { result }) => new User(mergeDeepRight(state, result)))
  .on(getUserMeFx.done, (state, { result }) => new User(mergeDeepRight(state, result)))
  .on(convertUserTypeFx.done, (state, { result }) => new User(mergeDeepRight(state, result)))
  .on([changeUser, updateUser], (state, result) => new User(mergeDeepRight(state, result)))
  .on(getUserFx.fail, () => defaultUser)
  .on(reset, () => defaultUser)

isLoaded
  .on($user.updates, () => true)
  .on(getUserFx.done, () => true)
  .on(updateUser, () => true)

profileIsLoaded.on(getUserProfileFx.done, () => true)

const isLoggedIn = $user.map<boolean>(({ id }) => id !== null && id > 0)

const $useXP: Store<boolean> = $user.map(({ isAgent }) => !isAgent)

sample({
  source: $windowUser,
  clock: initialize,
  target: $user,
})

forward({
  from: $windowUser,
  to: $user,
})

export { $user, $windowUser, isLoading, isLoaded, profileIsLoaded, $useXP, isLoggedIn, isAvatarLoading }

const data = combine({
  user: $user,
  useXP: $useXP,
  isLoading,
  isLoaded,
  isLoggedIn,
  profileIsLoaded,
  isAvatarLoading,
})

export { data }
